import { useEffect, useState } from 'react';
import "@/App.css";
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import { AppDashboard } from './components/AppDashboard';
import { LoginPage } from './components/auth/LoginPage';
import { SignupPage } from './components/auth/SignupPage';
import { ForgotPasswordPage } from './components/auth/ForgotPasswordPage';
import { ResetPasswordPage } from './components/auth/ResetPasswordPage';
import { Toaster } from './components/ui/sonner';
import { initScrollTracking } from './utils/analytics';
import { Loader2 } from 'lucide-react';
import ErrorBoundary from './components/ErrorBoundary';
import AboutPage from './components/pages/AboutPage';
import ContactPage from './components/pages/ContactPage';
import PrivacyPage from './components/pages/PrivacyPage';
import TermsPage from './components/pages/TermsPage';
import CookiePage from './components/pages/CookiePage';
import ComingSoonPage from './components/pages/ComingSoonPage';
import BillingSuccess from './components/pages/BillingSuccess';
import BillingCancel from './components/pages/BillingCancel';
import BillingCompleteSetup from './components/pages/BillingCompleteSetup';
import ProductSheetPage from './components/pages/ProductSheetPage';
import MarketingPage from './components/pages/MarketingPage';
import DemoEntryPage from './components/pages/DemoEntryPage';
import ClientInvitePage from './components/pages/ClientInvitePage';

const AppRoutes = () => {
  const { isAuthenticated, billingRequired, isDemo, loading, user } = useAuth();
  const isClient = user?.isClient === true || user?.role === 'client';
  const [route, setRoute] = useState(window.location.pathname);
  const [authView, setAuthView] = useState('login');

  useEffect(() => {
    const handleRouteChange = () => setRoute(window.location.pathname);
    window.addEventListener('popstate', handleRouteChange);
    return () => window.removeEventListener('popstate', handleRouteChange);
  }, []);

  useEffect(() => {
    if (route !== '/dashboard' && route !== '/app' && route !== '/login' && route !== '/signup') {
      const cleanup = initScrollTracking();
      return cleanup;
    }
  }, [route]);

  const navigate = (path) => {
    window.history.pushState({}, '', path);
    setRoute(path);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-indigo-400 animate-spin mx-auto mb-3" />
          <p className="text-zinc-500 text-sm">Loading D3 Command...</p>
        </div>
      </div>
    );
  }

  // Root route — show marketing landing page for all visitors
  // Authenticated users see the marketing page with "Go to Dashboard" in nav
  // Updated: Apr 2026 — single-domain deployment (app.d3command.com)
  if (route === '/' || route === '/company') {
    return (
      <ThemeProvider>
        <MarketingPage />
        <Toaster position="top-right" richColors />
      </ThemeProvider>
    );
  }

  // Product sheet — standalone public page for acquirers
  if (route === '/product-sheet') {
    return (
      <>
        <ProductSheetPage />
        <Toaster position="top-right" richColors />
      </>
    );
  }

  // Demo entry — auto-login and redirect to dashboard with voiceover tour
  if (route === '/demo') {
    return (
      <>
        <DemoEntryPage />
        <Toaster position="top-right" richColors />
      </>
    );
  }

  // Footer pages
  const footerPages = {
    '/about': AboutPage,
    '/contact': ContactPage,
    '/privacy-policy': PrivacyPage,
    '/terms-of-service': TermsPage,
    '/cookie-policy': CookiePage,
    '/blog': ComingSoonPage,
    '/careers': ComingSoonPage,
    '/documentation': ComingSoonPage,
    '/help-center': ComingSoonPage,
    '/case-studies': ComingSoonPage,
  };

  const FooterPageComponent = footerPages[route];
  if (FooterPageComponent) {
    return (
      <ThemeProvider>
        <div className="min-h-screen bg-[#0A0A0B] text-white">
          <Navbar onDashboardClick={() => navigate(isAuthenticated ? '/dashboard' : '/login')} />
          <FooterPageComponent />
          <Footer />
          <Toaster position="top-right" richColors />
        </div>
      </ThemeProvider>
    );
  }

  // Billing routes — accessible regardless of billing status
  if (route === '/billing/success') {
    return (
      <>
        <BillingSuccess navigate={navigate} />
        <Toaster position="top-right" richColors />
      </>
    );
  }
  if (route === '/billing/cancel') {
    return (
      <>
        <BillingCancel navigate={navigate} isAuthenticated={isAuthenticated} />
        <Toaster position="top-right" richColors />
      </>
    );
  }
  if (route === '/billing/complete-setup') {
    if (!isAuthenticated) {
      navigate('/login');
      return null;
    }
    return (
      <>
        <BillingCompleteSetup navigate={navigate} />
        <Toaster position="top-right" richColors />
      </>
    );
  }

  // ── BILLING GATE ──
  // If user is authenticated but has billingRequired=true,
  // redirect them to complete-setup (not dashboard)
  // Demo users and client users bypass billing gate
  if (isAuthenticated && billingRequired && !isDemo && !isClient) {
    if (route === '/dashboard' || route === '/app' || route.startsWith('/dashboard/')) {
      navigate('/billing/complete-setup');
      return null;
    }
  }

  if (route === '/dashboard' || route === '/app' || route.startsWith('/dashboard/')) {
    if (!isAuthenticated) {
      navigate('/login');
      return null;
    }
    return <AppDashboard />;
  }

  // Client invitation acceptance page (public)
  if (route === '/invite') {
    return (
      <>
        <ClientInvitePage navigate={navigate} />
        <Toaster position="top-right" richColors />
      </>
    );
  }

  // Reset password page (public — token from URL)
  if (route === '/reset-password') {
    return (
      <>
        <ResetPasswordPage />
        <Toaster position="top-right" richColors />
      </>
    );
  }

  if (route === '/login' || route === '/signup') {
    if (isAuthenticated) {
      // If billing is required (and not demo, not client), redirect to setup instead of dashboard
      if (billingRequired && !isDemo && !isClient) {
        navigate('/billing/complete-setup');
      } else {
        navigate('/dashboard');
      }
      return null;
    }
    if (authView === 'forgot') {
      return (
        <>
          <ForgotPasswordPage onBack={() => setAuthView('login')} />
          <Toaster position="top-right" richColors />
        </>
      );
    }
    if (authView === 'signup' || route === '/signup') {
      return (
        <>
          <SignupPage onSwitch={() => { setAuthView('login'); navigate('/login'); }} />
          <Toaster position="top-right" richColors />
        </>
      );
    }
    return (
      <>
        <LoginPage
          onSwitch={() => { setAuthView('signup'); navigate('/signup'); }}
          onForgot={() => setAuthView('forgot')}
        />
        <Toaster position="top-right" richColors />
      </>
    );
  }

  // Fallback — redirect based on hostname
  const hostname = window.location.hostname;
  if (hostname === 'app.d3command.com') {
    navigate(isAuthenticated ? '/dashboard' : '/login');
  } else {
    navigate('/');
  }
  return null;
};

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
